/**
 * CR130 Phase 3: Deal Cards Tab for Layout Studio
 * CR171: Added editable card heights with custom px values
 * CR225: Added read-only info panel for card composition (single source of truth)
 * CR319: Removed Details Content + Promo Badges (moved to Deal Cards → Structure)
 * 
 * Controls for card sizing, density, and details mode.
 * Changes are reflected in the live preview immediately.
 * 
 * NOTE: This tab controls PAGE-LEVEL UX settings (size, density, mode).
 * Card COMPOSITION (sections, labels, styling) is managed in Command Center → Deal Cards.
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Layers, 
  Maximize2, 
  Minimize2, 
  Square, 
  FlipHorizontal2, 
  PanelRight,
  Info,
  RotateCcw,
  AlertTriangle,
  FileImage,
} from "lucide-react";
import {
  type DealCardUXConfig,
  type CardSize,
  type CardDensity,
  type DetailsMode,
  type CustomHeightsPx,
  type FlyerInteractionMode,
  type FlyerInteractionConfig,
  type FeedbackPlacement,
  CARD_SIZE_LABELS,
  CARD_SIZE_HEIGHTS,
  DENSITY_LABELS,
  DETAILS_MODE_LABELS,
  DEFAULT_CUSTOM_HEIGHTS_PX,
  DEFAULT_FLYER_INTERACTION_CONFIG,
  DEFAULT_FEEDBACK_WIDGET_CONFIG,
  CARD_HEIGHT_CONSTRAINTS,
  validateCustomHeights,
} from "@/lib/layout-studio/dealCardUXTypes";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DealCardsTabProps {
  config: DealCardUXConfig;
  onChange: (config: DealCardUXConfig) => void;
}

interface DealCardsTabProps {
  config: DealCardUXConfig;
  onChange: (config: DealCardUXConfig) => void;
}

const SIZE_ICONS: Record<CardSize, React.ReactNode> = {
  compact: <Minimize2 className="h-4 w-4" />,
  regular: <Square className="h-4 w-4" />,
  large: <Maximize2 className="h-4 w-4" />,
};

const MODE_ICONS: Record<DetailsMode, React.ReactNode> = {
  flip: <FlipHorizontal2 className="h-4 w-4" />,
  expand: <Info className="h-4 w-4" />,
  panel: <PanelRight className="h-4 w-4" />,
};

export function DealCardsTab({ config, onChange }: DealCardsTabProps) {
  // CR171: Local state for debounced height inputs
  const [localHeights, setLocalHeights] = useState<CustomHeightsPx>(
    config.customHeightsPx ?? DEFAULT_CUSTOM_HEIGHTS_PX
  );
  
  // Sync local heights when config changes externally
  useEffect(() => {
    setLocalHeights(config.customHeightsPx ?? DEFAULT_CUSTOM_HEIGHTS_PX);
  }, [config.customHeightsPx]);
  
  // Debounced height update
  useEffect(() => {
    if (!config.customHeightsEnabled) return;
    
    const timeout = setTimeout(() => {
      // Only update if values differ
      if (
        localHeights.compact !== config.customHeightsPx?.compact ||
        localHeights.regular !== config.customHeightsPx?.regular ||
        localHeights.large !== config.customHeightsPx?.large
      ) {
        onChange({ ...config, customHeightsPx: localHeights });
      }
    }, 200);
    
    return () => clearTimeout(timeout);
  }, [localHeights, config.customHeightsEnabled]);
  
  const handleSizeChange = (size: CardSize) => {
    onChange({ ...config, cardSize: size });
  };

  const handleDensityChange = (density: CardDensity) => {
    onChange({ ...config, density });
  };

  const handleDetailsModeChange = (mode: DetailsMode) => {
    onChange({ ...config, detailsMode: mode });
  };

  const handleExpandSingleOpenChange = (checked: boolean) => {
    onChange({ ...config, expandSingleOpen: checked });
  };

  // CR451: Flyer interaction handlers
  const flyerConfig = config.flyerInteraction ?? DEFAULT_FLYER_INTERACTION_CONFIG;
  
  const handleFlyerInteractionChange = (mode: string) => {
    onChange({
      ...config,
      flyerInteraction: { ...flyerConfig, interaction: mode as FlyerInteractionMode },
    });
  };

  const handleFlyerBackdropCloseChange = (checked: boolean) => {
    onChange({
      ...config,
      flyerInteraction: { ...flyerConfig, allowBackdropClose: checked },
    });
  };

  const handleFlyerModalTitleChange = (title: string) => {
    onChange({
      ...config,
      flyerInteraction: { ...flyerConfig, modalTitle: title },
    });
  };

  // CR508: Feedback widget handlers
  const feedbackConfig = config.feedbackWidget ?? DEFAULT_FEEDBACK_WIDGET_CONFIG;

  const handleFeedbackEnabledChange = (enabled: boolean) => {
    onChange({
      ...config,
      feedbackWidget: {
        ...(config.feedbackWidget ?? DEFAULT_FEEDBACK_WIDGET_CONFIG),
        enabled,
      },
    });
  };

  const handleFeedbackPlacementChange = (placement: string) => {
    onChange({
      ...config,
      feedbackWidget: {
        ...(config.feedbackWidget ?? DEFAULT_FEEDBACK_WIDGET_CONFIG),
        placement: placement as FeedbackPlacement,
      },
    });
  };
  
  // CR171: Custom heights handlers
  const handleCustomHeightsToggle = (enabled: boolean) => {
    onChange({ 
      ...config, 
      customHeightsEnabled: enabled,
      // Initialize with defaults if enabling for first time
      customHeightsPx: config.customHeightsPx ?? DEFAULT_CUSTOM_HEIGHTS_PX,
    });
  };
  
  const handleHeightChange = (size: CardSize, value: number) => {
    setLocalHeights(prev => ({ ...prev, [size]: value }));
  };
  
  const handleResetHeights = () => {
    setLocalHeights(DEFAULT_CUSTOM_HEIGHTS_PX);
    onChange({ ...config, customHeightsPx: DEFAULT_CUSTOM_HEIGHTS_PX });
  };
  
  // Validation
  const heightErrors = config.customHeightsEnabled 
    ? validateCustomHeights(localHeights) 
    : null;
  const hasErrors = heightErrors !== null;
  
  // CR319: Section handlers removed (moved to Deal Cards → Structure)
  
  // Get effective height for display (custom or default)
  const getEffectiveHeight = (size: CardSize): number => {
    if (config.customHeightsEnabled && localHeights) {
      return localHeights[size];
    }
    return CARD_SIZE_HEIGHTS[size];
  };

  return (
    <div className="space-y-6">

      {/* Card Size */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Card Size
          </CardTitle>
          <CardDescription>
            Set a standardized height for all deal cards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={config.cardSize}
            onValueChange={(v) => handleSizeChange(v as CardSize)}
            className="grid grid-cols-3 gap-3"
          >
            {(Object.keys(CARD_SIZE_LABELS) as CardSize[]).map((size) => {
              const { label, description } = CARD_SIZE_LABELS[size];
              const height = getEffectiveHeight(size);
              const isSelected = config.cardSize === size;
              
              return (
                <TooltipProvider key={size}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label
                        htmlFor={`size-${size}`}
                        className={`
                          flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-muted-foreground/30"
                          }
                        `}
                      >
                        <RadioGroupItem value={size} id={`size-${size}`} className="sr-only" />
                        {SIZE_ICONS[size]}
                        <span className="font-medium text-sm">{label}</span>
                        <Badge variant="secondary" className="text-[10px]">
                          {height}px
                        </Badge>
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </RadioGroup>
          
          {/* CR171: Customize Heights Toggle */}
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="custom-heights" className="text-sm font-normal">
                Customize heights
              </Label>
              <p className="text-xs text-muted-foreground">
                Set custom pixel heights for each tier
              </p>
            </div>
            <Switch
              id="custom-heights"
              checked={config.customHeightsEnabled ?? false}
              onCheckedChange={handleCustomHeightsToggle}
            />
          </div>
          
          {/* CR171: Custom Height Inputs */}
          {config.customHeightsEnabled && (
            <div className="space-y-3 pt-2">
              {(["compact", "regular", "large"] as CardSize[]).map((size) => {
                const defaultHeight = CARD_SIZE_HEIGHTS[size];
                const error = heightErrors?.[size];
                
                return (
                  <div key={size} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-24">
                      {SIZE_ICONS[size]}
                      <span className="text-sm font-medium capitalize">{size}</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        type="number"
                        min={CARD_HEIGHT_CONSTRAINTS.min}
                        max={CARD_HEIGHT_CONSTRAINTS.max}
                        value={localHeights[size]}
                        onChange={(e) => handleHeightChange(size, parseInt(e.target.value) || defaultHeight)}
                        className={`h-8 w-24 text-sm ${error ? 'border-destructive' : ''}`}
                      />
                      <span className="text-xs text-muted-foreground">px</span>
                      {error && (
                        <span className="text-xs text-destructive">{error}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Order validation error */}
              {heightErrors?.order && (
                <div className="flex items-center gap-2 text-destructive text-xs">
                  <AlertTriangle className="h-3 w-3" />
                  <span>{heightErrors.order}</span>
                </div>
              )}
              
              {/* Helper text and reset button */}
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Range: {CARD_HEIGHT_CONSTRAINTS.min}–{CARD_HEIGHT_CONSTRAINTS.max}px
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetHeights}
                  className="h-7 text-xs"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Reset to defaults
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Density */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Density
          </CardTitle>
          <CardDescription>
            Control spacing and padding between elements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={config.density}
            onValueChange={(v) => handleDensityChange(v as CardDensity)}
            className="grid grid-cols-3 gap-3"
          >
            {(Object.keys(DENSITY_LABELS) as CardDensity[]).map((density) => {
              const { label, description } = DENSITY_LABELS[density];
              const isSelected = config.density === density;
              
              return (
                <TooltipProvider key={density}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label
                        htmlFor={`density-${density}`}
                        className={`
                          flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-muted-foreground/30"
                          }
                        `}
                      >
                        <RadioGroupItem value={density} id={`density-${density}`} className="sr-only" />
                        <div className={`flex gap-${density === "tight" ? 0.5 : density === "comfortable" ? 1 : 2}`}>
                          <div className="w-2 h-2 rounded-sm bg-muted-foreground/40" />
                          <div className="w-2 h-2 rounded-sm bg-muted-foreground/40" />
                          <div className="w-2 h-2 rounded-sm bg-muted-foreground/40" />
                        </div>
                        <span className="font-medium text-sm">{label}</span>
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Details Interaction */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            Details Interaction
          </CardTitle>
          <CardDescription>
            How users access additional deal information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={config.detailsMode}
            onValueChange={(v) => handleDetailsModeChange(v as DetailsMode)}
            className="grid grid-cols-3 gap-3"
          >
            {(Object.keys(DETAILS_MODE_LABELS) as DetailsMode[]).map((mode) => {
              const { label, description } = DETAILS_MODE_LABELS[mode];
              const isSelected = config.detailsMode === mode;
              
              return (
                <TooltipProvider key={mode}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label
                        htmlFor={`mode-${mode}`}
                        className={`
                          flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-muted-foreground/30"
                          }
                        `}
                      >
                        <RadioGroupItem value={mode} id={`mode-${mode}`} className="sr-only" />
                        {MODE_ICONS[mode]}
                        <span className="font-medium text-sm">{label}</span>
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </RadioGroup>

          {/* Expand mode option */}
          {config.detailsMode === "expand" && (
            <>
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="expand-single" className="text-sm font-normal">
                    Single card expanded
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Collapse other cards when opening a new one
                  </p>
                </div>
                <Switch
                  id="expand-single"
                  checked={config.expandSingleOpen ?? true}
                  onCheckedChange={handleExpandSingleOpenChange}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* CR451: Flyer Interaction */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileImage className="h-4 w-4" />
            Flyer Interaction
          </CardTitle>
          <CardDescription>
            Controls how the flyer image behaves when clicked on the card back.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="flyer-interaction" className="text-sm">Interaction</Label>
            <Select
              value={flyerConfig.interaction}
              onValueChange={handleFlyerInteractionChange}
            >
              <SelectTrigger id="flyer-interaction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Disabled</SelectItem>
                <SelectItem value="modal">Expand image (modal)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {flyerConfig.interaction === "none" && (
            <p className="text-xs text-muted-foreground italic">
              Thumbnail is not clickable.
            </p>
          )}

          {flyerConfig.interaction === "modal" && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="flyer-backdrop-close" className="text-sm font-normal">
                    Allow backdrop click to close
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Click outside the modal to dismiss it
                  </p>
                </div>
                <Switch
                  id="flyer-backdrop-close"
                  checked={flyerConfig.allowBackdropClose}
                  onCheckedChange={handleFlyerBackdropCloseChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="flyer-modal-title" className="text-sm">Modal title</Label>
                <Input
                  id="flyer-modal-title"
                  value={flyerConfig.modalTitle}
                  onChange={(e) => handleFlyerModalTitleChange(e.target.value)}
                  placeholder="Flyer preview"
                  className="h-8 text-sm"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* CR508: Feedback Widget Placement */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            Feedback Widget
          </CardTitle>
          <CardDescription>
            Global placement and visibility for the deal feedback control.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="feedback-widget-enabled" className="text-sm font-normal">
                Enable feedback widget
              </Label>
              <p className="text-xs text-muted-foreground">
                Applies to all deal cards in this layout preset.
              </p>
            </div>
            <Switch
              id="feedback-widget-enabled"
              checked={feedbackConfig.enabled}
              onCheckedChange={handleFeedbackEnabledChange}
            />
          </div>

          {feedbackConfig.enabled && (
            <div className="space-y-2">
              <Label htmlFor="feedback-widget-placement" className="text-sm">Placement</Label>
              <Select
                value={feedbackConfig.placement}
                onValueChange={handleFeedbackPlacementChange}
              >
                <SelectTrigger id="feedback-widget-placement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inside_card">Inside card</SelectItem>
                  <SelectItem value="above_card">Above card (center)</SelectItem>
                  <SelectItem value="top_left">Top left</SelectItem>
                  <SelectItem value="top_right">Top right</SelectItem>
                  <SelectItem value="below_left">Below card (left)</SelectItem>
                  <SelectItem value="below_right">Below card (right)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Label text is still managed in Deal Cards → Components → Feedback.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
